import { useState } from "react";
import { api, fileUrl } from "@/lib/api";
import { toast } from "sonner";
import { PackageCheck, Truck, Clock } from "lucide-react";

const STATUS_LABELS = {
    pending: { label: "En attente de paiement", icon: Clock },
    paid: { label: "Payée — préparation", icon: PackageCheck },
    shipped: { label: "Expédiée", icon: Truck },
    delivered: { label: "Livrée", icon: PackageCheck },
    refunded: { label: "Remboursée", icon: Clock },
};

export default function TrackPage() {
    const [orderNumber, setOrderNumber] = useState("");
    const [email, setEmail] = useState("");
    const [order, setOrder] = useState(null);
    const [loading, setLoading] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setOrder(null);
        try {
            const r = await api.get("/orders/track", { params: { order_number: orderNumber, email } });
            setOrder(r.data);
        } catch {
            toast.error("Commande introuvable. Vérifiez le numéro et le courriel.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-16" data-testid="track-page">
            <h1 className="font-serif-luxe text-3xl sm:text-5xl chrome-text font-semibold mb-4">Suivi de commande</h1>
            <p className="text-slate-400 mb-10">Entrez votre numéro de commande (format VVS-…) et votre courriel.</p>

            <form onSubmit={submit} className="glass-card rounded-2xl p-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                    <label>Numéro de commande</label>
                    <input data-testid="track-order-number" required value={orderNumber} onChange={(e) => setOrderNumber(e.target.value)} placeholder="VVS-20260101-ABC123" />
                </div>
                <div>
                    <label>Courriel</label>
                    <input data-testid="track-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
                </div>
                <div className="sm:col-span-2">
                    <button data-testid="track-submit-btn" type="submit" disabled={loading} className="btn-primary w-full">
                        {loading ? "Recherche…" : "Suivre ma commande"}
                    </button>
                </div>
            </form>

            {order && (
                <div data-testid="track-result" className="mt-10 glass-card rounded-2xl p-6 fade-in-up">
                    <div className="flex items-center justify-between">
                        <div>
                            <div className="text-xs uppercase tracking-widest text-slate-500">Commande</div>
                            <div data-testid="track-order-number-display" className="font-serif-luxe text-2xl text-slate-100">{order.order_number}</div>
                        </div>
                        <div className="text-right">
                            <div className="text-xs uppercase tracking-widest text-slate-500">Statut</div>
                            <div data-testid="track-status" className="text-sm font-semibold text-slate-100 mt-1">
                                {STATUS_LABELS[order.status]?.label || order.status}
                            </div>
                        </div>
                    </div>

                    <div className="hair-divider my-5" />

                    <div className="space-y-3">
                        {order.items.map((it, i) => (
                            <div key={i} className="flex items-center gap-3">
                                {it.image && <img src={fileUrl(it.image)} alt="" className="w-12 h-12 object-cover rounded-lg bg-black/40" />}
                                <div className="flex-1">
                                    <div className="text-sm text-slate-100">{it.name}</div>
                                    <div className="text-xs text-slate-500">{it.variant_label} × {it.quantity}</div>
                                </div>
                                <div className="text-sm text-slate-200">{(it.price_cad * it.quantity).toFixed(2)} $</div>
                            </div>
                        ))}
                    </div>

                    <div className="hair-divider my-5" />
                    <div className="flex justify-between text-sm">
                        <span className="text-slate-400">Total payé</span>
                        <span data-testid="track-total" className="font-semibold text-slate-100">{order.total_cad.toFixed(2)} $ CAD</span>
                    </div>

                    {order.tracking_number && (
                        <div className="mt-5 bg-white/5 border border-white/10 rounded-xl p-4">
                            <div className="text-xs uppercase tracking-widest text-slate-500 mb-1">Postes Canada — suivi</div>
                            <div data-testid="track-tracking-number" className="text-sm font-mono-tight text-slate-100">{order.tracking_number}</div>
                            {order.tracking_url && (
                                <a data-testid="track-tracking-link" href={order.tracking_url} target="_blank" rel="noopener noreferrer" className="text-xs text-slate-300 underline mt-2 inline-block">
                                    Ouvrir le suivi officiel
                                </a>
                            )}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
